import React, { useState, useEffect } from 'react';
import SchoolWorkflows from './management/SchoolWorkflows';
import { useAuth } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Label } from './ui/label';
import { 
  GraduationCap, 
  BarChart3, 
  Calendar, 
  LogOut,
  Users,
  BookOpen,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  User,
  Phone,
  Mail,
  MapPin,
  Heart,
  CreditCard,
  ChevronDown,
  Package,
  History,
  Receipt,
  Download,
  Menu,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import WeeklyPlanningTab from './WeeklyPlanningTab';
import ParentCalendarsView from './ParentCalendarsView';
import ParentPackages from './parent/ParentPackages';

const ParentInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payingId, setPayingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/invoices/my');
      setInvoices(res.data || []);
    } catch (e) {
      toast.error('Error al cargar facturas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Verify a Stripe return if present
    const params = new URLSearchParams(window.location.search);
    const invoicePaid = params.get('invoice_paid');
    const sessionId = params.get('session_id');
    const verify = async () => {
      if (invoicePaid && invoicePaid !== '1' && sessionId) {
        try {
          const res = await axios.post(`/invoices/${invoicePaid}/verify-payment`, { session_id: sessionId });
          if (res.data.status === 'paid') toast.success('¡Pago confirmado! Gracias.');
          window.history.replaceState({}, '', window.location.pathname);
        } catch { /* ignore */ }
      }
      load();
    };
    verify();
  }, []);

  const payInvoice = async (inv) => {
    setPayingId(inv.id);
    try {
      const res = await axios.post(`/invoices/${inv.id}/pay`, { origin: window.location.origin });
      if (res.data.checkout_url) window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al iniciar el pago');
      setPayingId(null);
    }
  };

  const downloadPdf = async (inv) => {
    try {
      const res = await axios.get(`/invoices/my/${inv.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `Factura_${inv.invoice_number}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } catch {
      toast.error('Error al descargar PDF');
    }
  };

  const statusBadge = (s) => {
    if (s === 'paid') return <Badge className="bg-green-100 text-green-800">Pagada</Badge>;
    if (s === 'overdue') return <Badge className="bg-red-100 text-red-800">Vencida</Badge>;
    return <Badge className="bg-yellow-100 text-yellow-800">Pendiente</Badge>;
  };

  const pendingTotal = invoices.filter(i => i.payment_status !== 'paid').reduce((a, i) => a + (i.total || 0), 0);

  return (
    <div className="space-y-6" data-testid="parent-invoices">
      <div className="flex items-center gap-3">
        <div className="w-1 h-6 bg-gradient-to-b from-indigo-400 to-indigo-600 rounded-full"></div>
        <div>
          <h2 className="text-xl font-bold text-slate-800">Mis Facturas</h2>
          <p className="text-sm text-slate-500">Consulta y paga las facturas de tus hijos/as</p>
        </div>
      </div>

      {pendingTotal > 0 && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="p-4 flex items-center justify-between">
            <span className="text-sm text-yellow-800">Total pendiente por pagar</span>
            <span className="text-2xl font-bold text-yellow-700" data-testid="pending-total">${pendingTotal.toFixed(2)}</span>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12"><div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : invoices.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-400">
          <Receipt className="w-12 h-12 mx-auto mb-2 text-gray-300" />
          <p>No tienes facturas registradas</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {invoices.map(inv => (
            <Card key={inv.id} data-testid={`invoice-${inv.id}`}>
              <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-slate-800">{inv.invoice_number}</p>
                    {statusBadge(inv.payment_status)}
                    {inv.billing_month && <span className="text-xs text-slate-400">{inv.billing_month}</span>}
                  </div>
                  <p className="text-sm text-slate-500 mt-1">{(inv.items?.[0]?.description) || inv.client_name}</p>
                  <p className="text-xs text-slate-400">Vence: {inv.due_date || 'N/A'}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xl font-bold text-slate-800">${(inv.total || 0).toFixed(2)}</span>
                  <Button variant="outline" size="sm" onClick={() => downloadPdf(inv)} data-testid={`invoice-pdf-${inv.id}`}>
                    <Download className="w-4 h-4" />
                  </Button>
                  {inv.payment_status !== 'paid' && (
                    <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => payInvoice(inv)} disabled={payingId === inv.id} data-testid={`invoice-pay-${inv.id}`}>
                      <CreditCard className="w-4 h-4 mr-1" /> {payingId === inv.id ? 'Redirigiendo...' : 'Pagar'}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};


const ParentDashboard = () => {
  const { logout, user, schoolInfo } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Auto-navigate to packages tab on Stripe redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment')) {
      setActiveTab('packages');
    }
  }, []);
  const [loading, setLoading] = useState(false);
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState('');
  const [childGrades, setChildGrades] = useState([]);
  const [viewedGradeIds, setViewedGradeIds] = useState(new Set());
  const [childAttendance, setChildAttendance] = useState([]);
  const [stats, setStats] = useState({});
  const [subjects, setSubjects] = useState([]);
  const [expandedSubjects, setExpandedSubjects] = useState({});
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState('all');

  useEffect(() => {
    fetchChildren();
    fetchSubjects();
  }, []);

  useEffect(() => {
    if (selectedChild) {
      fetchChildStats();
      fetchChildGrades();
      fetchChildAttendance();
      fetchAvailableYears();
    }
  }, [selectedChild]);

  const fetchChildren = async () => {
    try {
      setLoading(true);
      // Fetch students where parent_user_id matches current user
      const response = await axios.get(`/students?parent_user_id=${user.user_id}`);
      setChildren(response.data);
      if (response.data.length > 0) {
        setSelectedChild(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching children:', error);
      toast.error('Error al cargar información de estudiantes');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await axios.get('/subjects');
      setSubjects(response.data);
    } catch (error) {
      console.error('Error fetching subjects:', error);
    }
  };

  const fetchChildStats = async () => {
    if (!selectedChild) return;
    
    try {
      const gradesResponse = await axios.get(`/grades/student/${selectedChild}`);
      const attendanceResponse = await axios.get(`/attendance/student/${selectedChild}`);
      
      const grades = (gradesResponse.data.grades || []).filter(g => !g.is_subclass);
      const attendance = attendanceResponse.data || [];
      
      // Agrupar calificaciones por materia y calcular promedio de cada materia
      const gradesBySubject = grades.reduce((acc, grade) => {
        if (!acc[grade.subject]) {
          acc[grade.subject] = [];
        }
        acc[grade.subject].push(grade);
        return acc;
      }, {});
      
      // Calcular letra de cada materia y convertir a GPA
      let totalGPA = 0;
      let subjectCount = 0;
      
      Object.values(gradesBySubject).forEach(subjectGrades => {
        // Normalizar cada evaluación a porcentaje INDIVIDUALMENTE antes de promediar
        // (mismo método usado en la pestaña Calificaciones) — evita que este resumen
        // difiera del detalle por materia cuando las evaluaciones usan escalas distintas.
        const gradePercentages = subjectGrades.map(g => {
          let mp = g.max_points;
          if (!mp) mp = g.grade_value > 10 ? 100 : 10;
          return (g.grade_value / mp) * 100;
        });
        const percentage = Math.round(gradePercentages.reduce((sum, p) => sum + p, 0) / gradePercentages.length);
        
        // Determinar letra
        let letter = 'F';
        if (percentage >= 90) letter = 'A';
        else if (percentage >= 80) letter = 'B';
        else if (percentage >= 70) letter = 'C';
        else if (percentage >= 60) letter = 'D';
        
        // Convertir letra a número para GPA (A=4, B=3, C=2, D=1, F=0)
        const letterToGPA = { 'A': 4, 'B': 3, 'C': 2, 'D': 1, 'F': 0 };
        totalGPA += letterToGPA[letter];
        subjectCount++;
      });
      
      // Calcular GPA promedio
      const averageGPA = subjectCount > 0 ? (totalGPA / subjectCount).toFixed(2) : '0.00';
      
      const totalAttendance = attendance.length;
      const presentDays = attendance.filter(a => a.status === 'present').length;
      const attendanceRate = totalAttendance > 0 
        ? ((presentDays / totalAttendance) * 100).toFixed(1)
        : 0;
      
      setStats({
        totalGrades: grades.length,
        averageGrade: averageGPA,
        attendanceRate,
        totalAttendance,
        presentDays
      });
    } catch (error) {
      console.error('Error fetching child stats:', error);
    }
  };

  const fetchChildGrades = async () => {
    if (!selectedChild) return;
    
    try {
      const response = await axios.get(`/grades/student/${selectedChild}`);
      setChildGrades(response.data.grades || []);
      const viewsRes = await axios.get('/grades/views/mine');
      setViewedGradeIds(new Set(viewsRes.data.grade_ids || []));
    } catch (error) {
      console.error('Error fetching child grades:', error);
      toast.error('Error al cargar calificaciones');
    }
  };

  const markGradeViewed = async (gradeId) => {
    try {
      await axios.post(`/grades/${gradeId}/view`);
      setViewedGradeIds(prev => new Set(prev).add(gradeId));
      toast.success('Marcada como vista');
    } catch (error) {
      toast.error('No se pudo marcar como vista');
    }
  };

  const fetchChildAttendance = async () => {
    if (!selectedChild) return;
    
    try {
      const response = await axios.get(`/attendance/student/${selectedChild}`);
      setChildAttendance(response.data);
    } catch (error) {
      console.error('Error fetching child attendance:', error);
      toast.error('Error al cargar asistencia');
    }
  };

  const fetchAvailableYears = async () => {
    if (!selectedChild) return;
    try {
      const response = await axios.get(`/grades/student/${selectedChild}/years`);
      setAvailableYears(response.data.years || []);
    } catch {
      setAvailableYears([]);
    }
  };

  const getSelectedChildInfo = () => {
    return children.find(child => child.id === selectedChild);
  };

  // Convertir calificación a GPA (escala 4.0)
  const gradeToGPA = (grade, maxGrade = 10) => {
    if (maxGrade <= 4) return grade;
    return (grade / maxGrade) * 4.0;
  };

  // Convertir GPA a letra (sistema Puerto Rico/USA)
  const gpaToLetter = (gpa) => {
    if (gpa >= 3.5) return 'A';
    if (gpa >= 2.5) return 'B';
    if (gpa >= 1.5) return 'C';
    if (gpa >= 0.5) return 'D';
    return 'F';
  };

  const getGradeColor = (letter) => {
    if (letter === 'A') return 'bg-green-100 text-green-800';
    if (letter === 'B') return 'bg-blue-100 text-blue-800';
    if (letter === 'C') return 'bg-yellow-100 text-yellow-800';
    if (letter === 'D') return 'bg-orange-100 text-orange-800';
    return 'bg-red-100 text-red-800';
  };

  const getAttendanceIcon = (status) => {
    switch (status) {
      case 'present': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'absent': return <XCircle className="w-4 h-4 text-red-600" />;
      case 'late': return <Clock className="w-4 h-4 text-yellow-600" />;
      default: return <Clock className="w-4 h-4 text-slate-500" />;
    }
  };

  const StatCard = ({ title, value, icon: Icon, color, description }) => (
    <Card className="modern-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-slate-500 mb-1">{title}</p>
            <p className="text-2xl font-bold text-slate-800">{value}</p>
            {description && <p className="text-xs text-slate-400 mt-1">{description}</p>}
          </div>
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="flex min-h-screen">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          data-testid="parent-sidebar-overlay"
        />
      )}
      {/* Sidebar */}
      <div className={`dashboard-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-content">
          <div className="p-5 pb-3">
            <div className="flex items-center mb-5">
              <div className="w-14 h-14 rounded-xl mr-3 flex items-center justify-center shrink-0"
                style={schoolInfo?.school_logo_url
                  ? { background: '#ffffff', padding: '3px' }
                  : { background: 'linear-gradient(135deg, #f97316, #ea580c)' }}>
                {schoolInfo?.school_logo_url ? (
                  <img src={schoolInfo.school_logo_url.startsWith('data:') ? schoolInfo.school_logo_url : `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}${schoolInfo.school_logo_url}`} alt="" className="w-full h-full object-contain rounded-lg" />
                ) : (
                  <GraduationCap className="w-7 h-7 text-white" />
                )}
              </div>
              <div>
                <h1 className="text-base font-bold text-white tracking-tight">
                  {schoolInfo?.school_name || (
                    <span>
                      EduCore
                      <span className="relative inline-block">
                        <span>®</span>
                        <span className="absolute left-full bottom-1/2 text-[6px] font-bold tracking-[0.1em] text-slate-400 leading-none ml-0.5">BRAND</span>
                      </span>
                    </span>
                  )}
                </h1>
                <p className="text-[11px] text-slate-400 font-medium">Padre de Familia</p>
              </div>
            </div>
            
            <Button 
              onClick={logout}
              variant="outline" 
              size="sm"
              className="w-full bg-white/5 border-white/10 text-slate-300 hover:bg-red-500/20 hover:text-red-300 hover:border-red-400/30 transition-all text-xs mb-2"
            >
              <LogOut className="w-3 h-3 mr-1" />
              Cerrar Sesión
            </Button>
          </div>

          <div className="sidebar-nav">
            <nav className="space-y-2">
              <button
                onClick={() => setActiveTab('overview')}
                className={`sidebar-nav-item w-full text-left ${activeTab === 'overview' ? 'active' : ''}`}
              >
                <BarChart3 className="w-5 h-5 mr-3" />
                Dashboard
              </button>
              <button
                onClick={() => setActiveTab('grades')}
                className={`sidebar-nav-item w-full text-left ${activeTab === 'grades' ? 'active' : ''}`}
              >
                <BookOpen className="w-5 h-5 mr-3" />
                Calificaciones
              </button>
              <button
                onClick={() => setActiveTab('attendance')}
                className={`sidebar-nav-item w-full text-left ${activeTab === 'attendance' ? 'active' : ''}`}
              >
                <Calendar className="w-5 h-5 mr-3" />
                Asistencia
              </button>
              <button onClick={()=>{setActiveTab('applications');setSidebarOpen(false);}} className={`sidebar-nav-item w-full text-left ${activeTab === 'applications'?'active':''}`}>Matrícula y rematrícula</button>
              <button
                onClick={() => setActiveTab('profile')}
                className={`sidebar-nav-item w-full text-left ${activeTab === 'profile' ? 'active' : ''}`}
              >
                <User className="w-5 h-5 mr-3" />
                Perfil del Estudiante
              </button>
              <button
                onClick={() => setActiveTab('invoices')}
                className={`sidebar-nav-item w-full text-left ${activeTab === 'invoices' ? 'active' : ''}`}
                data-testid="parent-nav-invoices"
              >
                <Receipt className="w-5 h-5 mr-3" />
                Facturas
              </button>
              <button
                onClick={() => setActiveTab('calendars')}
                className={`sidebar-nav-item w-full text-left ${activeTab === 'calendars' ? 'active' : ''}`}
                data-testid="parent-nav-calendars"
              >
                <Calendar className="w-5 h-5 mr-3" />
                Calendarios
              </button>
              {(user?.is_primary_parent !== false) && (
                <button
                  onClick={() => setActiveTab('packages')}
                  className={`sidebar-nav-item w-full text-left ${activeTab === 'packages' ? 'active' : ''}`}
                  data-testid="parent-nav-packages"
                >
                  <Package className="w-5 h-5 mr-3" />
                  Realizar Pagos
                </button>
              )}
            </nav>
          </div>

          <div className="sidebar-footer">
            <div className="bg-white/5 rounded-xl p-3 border border-white/5">
              <p className="text-xs text-white font-medium">{user?.username}</p>
              <p className="text-[11px] text-slate-400">Padre de Familia</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="dashboard-main">
        <div className="mb-8">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden fixed top-4 left-4 z-40 bg-slate-800 hover:bg-slate-700 text-white p-2 rounded-lg shadow-lg"
            data-testid="parent-menu-toggle"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <div className="flex flex-col gap-4 lg:flex-row lg:justify-between lg:items-start">
            <div className="ml-14 lg:ml-0">
              <div className="flex items-center gap-3 mb-1">
                <div className="w-1 h-8 bg-gradient-to-b from-orange-400 to-orange-600 rounded-full"></div>
                <h1 className="text-2xl font-bold text-slate-800 tracking-tight">
                  Portal de Padres
                </h1>
              </div>
              <p className="text-sm text-slate-500 ml-5">
                Consulta el progreso académico de tu hijo/a
              </p>
            </div>
            
            {children.length > 0 && (
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full lg:w-auto">
                <label className="text-sm font-medium text-slate-600">Estudiante:</label>
                <Select value={selectedChild} onValueChange={setSelectedChild}>
                  <SelectTrigger className="w-full sm:w-48">
                    <SelectValue placeholder="Seleccionar hijo/a" />
                  </SelectTrigger>
                  <SelectContent>
                    {children.map((child) => (
                      <SelectItem key={child.id} value={child.id}>
                        {child.assigned_grade}: {`${child.first_name || ''} ${child.last_name_paternal || ''}`.trim()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>

        {children.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-800 mb-2">Sin estudiantes asignados</h3>
            <p className="text-slate-500">No tienes estudiantes asignados a tu cuenta. Contacta al administrador.</p>
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === 'applications' && <SchoolWorkflows parent children={children} />}
            {activeTab === 'overview' && (
              <div className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <StatCard
                    title="Promedio General"
                    value={stats.averageGrade || '0.0'}
                    icon={TrendingUp}
                    color="bg-gradient-to-br from-blue-500 to-blue-600"
                    description="Promedio de calificaciones"
                  />
                  <StatCard
                    title="Total Calificaciones"
                    value={stats.totalGrades || 0}
                    icon={BookOpen}
                    color="bg-gradient-to-br from-indigo-500 to-indigo-600"
                    description="Calificaciones registradas"
                  />
                  <StatCard
                    title="Asistencia"
                    value={`${stats.attendanceRate || 0}%`}
                    icon={CheckCircle}
                    color="bg-gradient-to-br from-green-500 to-green-600"
                    description={`${stats.presentDays || 0} de ${stats.totalAttendance || 0} días`}
                  />
                  <StatCard
                    title="Grado"
                    value={getSelectedChildInfo()?.assigned_grade || 'N/A'}
                    icon={GraduationCap}
                    color="bg-gradient-to-br from-purple-500 to-purple-600"
                    description="Año académico actual"
                  />
                </div>

                {/* Recent Grades */}
                <Card className="modern-card">
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <BookOpen className="w-5 h-5 mr-2 text-blue-600" />
                      Calificaciones Recientes
                    </CardTitle>
                    <CardDescription>
                      Últimas 5 calificaciones registradas
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {childGrades.length > 0 ? (
                      <div className="space-y-4">
                        {childGrades.slice(-5).reverse().map((grade, index) => (
                          <div key={grade.id || `${grade.subject}-${grade.date_recorded}-${index}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium text-slate-800">{grade.subject}</p>
                              <p className="text-sm text-slate-500">
                                {grade.created_at ? new Date(grade.created_at).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' }) : grade.date_recorded ? new Date(grade.date_recorded).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' }) : ''} • {grade.description}
                              </p>
                            </div>
                            <Badge className={getGradeColor(grade.grade_value)}>
                              {grade.grade_value}/{grade.max_points || 100}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-400 text-center py-4">No hay calificaciones registradas</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Grades Tab */}
            {activeTab === 'grades' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center flex-wrap gap-3">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-800">Calificaciones</h2>
                    <p className="text-slate-500">Calificaciones agrupadas por materia</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {availableYears.length > 1 && (
                      <div className="flex items-center gap-2">
                        <Label className="text-sm text-slate-600 whitespace-nowrap">Ano Academico:</Label>
                        <Select value={selectedAcademicYear} onValueChange={setSelectedAcademicYear}>
                          <SelectTrigger className="w-48" data-testid="parent-year-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Todos los anos</SelectItem>
                            {availableYears.map((year) => (
                              <SelectItem key={year} value={year}>{year}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  {childGrades.length > 0 ? (
                    // Group grades by academic year then by subject
                    (() => {
                      const filtered = selectedAcademicYear === 'all'
                        ? childGrades
                        : childGrades.filter(g => g.academic_year === selectedAcademicYear);

                      if (filtered.length === 0) {
                        return (
                          <Card className="modern-card">
                            <CardContent className="p-8 text-center text-slate-400">
                              No hay calificaciones para este ano academico
                            </CardContent>
                          </Card>
                        );
                      }

                      // Group by year first
                      const byYear = {};
                      filtered.forEach(grade => {
                        const year = grade.academic_year || 'Sin ano';
                        if (!byYear[year]) byYear[year] = {};
                        if (!byYear[year][grade.subject]) byYear[year][grade.subject] = [];
                        byYear[year][grade.subject].push(grade);
                      });

                      const sortedYears = Object.keys(byYear).sort().reverse();

                      return sortedYears.map((year) => {
                        const subjectGroups = byYear[year];
                        return (
                          <div key={year} className="space-y-4">
                            {(selectedAcademicYear === 'all' && availableYears.length > 1) && (
                              <div className="flex items-center gap-2 pt-2">
                                <History className="w-5 h-5 text-blue-600" />
                                <h3 className="text-lg font-semibold text-slate-700">{year}</h3>
                              </div>
                            )}
                            {Object.entries(subjectGroups).map(([subject, grades]) => {
                        // Calcular promedio normalizando cada evaluación a porcentaje primero
                        // (las evaluaciones de una misma materia pueden tener escalas distintas: X/5, X/10, X/20...)
                        const gradePercentages = grades.map(g => {
                          let mp = g.max_points;
                          if (!mp) mp = g.grade_value > 10 ? 100 : 10;
                          return (g.grade_value / mp) * 100;
                        });
                        const percentage = Math.round(gradePercentages.reduce((sum, p) => sum + p, 0) / gradePercentages.length);

                        // Determinar letra basada en porcentaje
                        let letter = 'F';
                        if (percentage >= 90) letter = 'A';
                        else if (percentage >= 80) letter = 'B';
                        else if (percentage >= 70) letter = 'C';
                        else if (percentage >= 60) letter = 'D';
                        
                        const isExpanded = expandedSubjects[subject] !== false; // Expandido por defecto
                        
                        return (
                          <Card key={subject} className="modern-card">
                            <CardHeader 
                              className="bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                              onClick={() => setExpandedSubjects(prev => ({
                                ...prev,
                                [subject]: !isExpanded
                              }))}
                            >
                              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                                  <ChevronDown 
                                    className={`w-5 h-5 shrink-0 transition-transform ${isExpanded ? 'rotate-0' : '-rotate-90'}`}
                                  />
                                  <CardTitle className="text-lg truncate">{subject}</CardTitle>
                                  {grades[0]?.is_subclass && <Badge variant="secondary">Subclase · No cuenta para nota</Badge>}
                                  <span className="text-sm text-slate-400 whitespace-nowrap">({grades.length} evaluaciones)</span>
                                </div>
                                <div className="flex items-center gap-2 pl-7 sm:pl-0">
                                  <span className="text-sm text-slate-500">{grades[0]?.is_subclass ? 'Seguimiento:' : 'Promedio:'}</span>
                                  <Badge className={getGradeColor(letter)} style={{fontSize: '1rem', padding: '0.5rem 1rem'}}>
                                    {letter} ({percentage}%)
                                  </Badge>
                                </div>
                              </div>
                            </CardHeader>
                            {isExpanded && (
                              <CardContent className="p-0">
                                {/* Desktop/tablet table */}
                                <div className="hidden md:block overflow-x-auto">
                                  <table className="w-full">
                                    <thead className="bg-gray-100">
                                      <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                                          Evaluación
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                                          Calificación
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                                          Anotaciones del Profesor
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                                          Fecha
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                                          Visto
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                      {grades.map((grade, index) => {
                                        // Detectar automáticamente la escala basándose en el valor
                                        let maxPoints = grade.max_points;
                                        if (!maxPoints) {
                                          // Si el valor es mayor a 10, asumimos escala de 100
                                          maxPoints = grade.grade_value > 10 ? 100 : 10;
                                        }
                                        
                                        // Calcular porcentaje
                                        const gPercentage = (grade.grade_value / maxPoints) * 100;
                                        
                                        // Determinar color basado en la calificación original
                                        let badgeColor = 'bg-red-100 text-red-800';
                                        if (gPercentage >= 90) badgeColor = 'bg-green-100 text-green-800';
                                        else if (gPercentage >= 80) badgeColor = 'bg-blue-100 text-blue-800';
                                        else if (gPercentage >= 70) badgeColor = 'bg-yellow-100 text-yellow-800';
                                        
                                        return (
                                          <tr key={grade.id || `${grade.subject}-${grade.date_recorded}-${index}`} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-800">
                                              {grade.description || 'Sin descripción'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                              <Badge className={badgeColor}>
                                                {grade.grade_value}/{maxPoints} ({gPercentage.toFixed(0)}%)
                                              </Badge>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-slate-600" data-testid={`grade-notes-${index}`}>
                                              {grade.notes ? (
                                                <div className="flex items-start gap-2">
                                                  <BookOpen className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                                                  <span>{grade.notes}</span>
                                                </div>
                                              ) : (
                                                <span className="text-slate-300 italic">—</span>
                                              )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                                              {new Date(grade.date_recorded).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                              {viewedGradeIds.has(grade.id) ? (
                                                <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-medium" data-testid={`grade-viewed-badge-${index}`}>
                                                  <Eye className="w-3.5 h-3.5" /> Vista
                                                </span>
                                              ) : (
                                                <button
                                                  onClick={() => markGradeViewed(grade.id)}
                                                  className="text-xs text-blue-600 hover:text-blue-800 font-medium underline"
                                                  data-testid={`mark-grade-viewed-${index}`}
                                                >
                                                  Marcar como vista
                                                </button>
                                              )}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                                {/* Mobile stacked list */}
                                <div className="md:hidden divide-y divide-gray-200" data-testid={`grades-mobile-list-${subject}`}>
                                  {grades.map((grade, index) => {
                                    let maxPoints = grade.max_points;
                                    if (!maxPoints) {
                                      maxPoints = grade.grade_value > 10 ? 100 : 10;
                                    }
                                    const gPercentage = (grade.grade_value / maxPoints) * 100;
                                    let badgeColor = 'bg-red-100 text-red-800';
                                    if (gPercentage >= 90) badgeColor = 'bg-green-100 text-green-800';
                                    else if (gPercentage >= 80) badgeColor = 'bg-blue-100 text-blue-800';
                                    else if (gPercentage >= 70) badgeColor = 'bg-yellow-100 text-yellow-800';
                                    return (
                                      <div key={grade.id || `${grade.subject}-${grade.date_recorded}-${index}`} className="p-4 space-y-2" data-testid={`grade-card-${subject}-${index}`}>
                                        <div className="flex items-start justify-between gap-2">
                                          <p className="text-sm font-medium text-slate-800 min-w-0 break-words">{grade.description || 'Sin descripción'}</p>
                                          <Badge className={`${badgeColor} shrink-0 whitespace-nowrap`}>
                                            {grade.grade_value}/{maxPoints} ({gPercentage.toFixed(0)}%)
                                          </Badge>
                                        </div>
                                        {grade.notes && (
                                          <div className="flex items-start gap-2 text-sm text-slate-600">
                                            <BookOpen className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                                            <span className="break-words">{grade.notes}</span>
                                          </div>
                                        )}
                                        <p className="text-xs text-slate-400">
                                          {new Date(grade.date_recorded).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}
                                        </p>
                                        {viewedGradeIds.has(grade.id) ? (
                                          <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-medium" data-testid={`grade-viewed-badge-mobile-${index}`}>
                                            <Eye className="w-3.5 h-3.5" /> Vista
                                          </span>
                                        ) : (
                                          <button
                                            onClick={() => markGradeViewed(grade.id)}
                                            className="text-xs text-blue-600 hover:text-blue-800 font-medium underline"
                                            data-testid={`mark-grade-viewed-mobile-${index}`}
                                          >
                                            Marcar como vista
                                          </button>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </CardContent>
                            )}
                          </Card>
                        );
                      })}
                          </div>
                        );
                      });
                    })()
                  ) : (
                    <Card className="modern-card">
                      <CardContent className="p-8 text-center text-slate-400">
                        No hay calificaciones registradas
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            )}

            {/* Attendance Tab */}
            {activeTab === 'attendance' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-800">Asistencia</h2>
                    <p className="text-slate-500">Registro de asistencia escolar</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <Card className="modern-card">
                    <CardContent className="p-6 text-center">
                      <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-slate-800">{stats.presentDays || 0}</p>
                      <p className="text-sm text-slate-500">Días Presentes</p>
                    </CardContent>
                  </Card>
                  <Card className="modern-card">
                    <CardContent className="p-6 text-center">
                      <XCircle className="w-8 h-8 text-red-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-slate-800">
                        {childAttendance.filter(a => a.status === 'absent').length}
                      </p>
                      <p className="text-sm text-slate-500">Ausencias</p>
                    </CardContent>
                  </Card>
                  <Card className="modern-card">
                    <CardContent className="p-6 text-center">
                      <Clock className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-slate-800">
                        {childAttendance.filter(a => a.status === 'late').length}
                      </p>
                      <p className="text-sm text-slate-500">Llegadas Tarde</p>
                    </CardContent>
                  </Card>
                </div>

                <Card className="modern-card">
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                              Fecha
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                              Estado
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                              Notas
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {childAttendance.length > 0 ? childAttendance.slice(-10).reverse().map((attendance, index) => (
                            <tr key={attendance.id || `${attendance.date}-${index}`} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-800">
                                {new Date(attendance.date).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  {getAttendanceIcon(attendance.status)}
                                  <span className="ml-2 capitalize">{attendance.status}</span>
                                </div>
                              </td>
                              <td className="px-6 py-4 text-sm text-slate-500">
                                {attendance.notes || 'Sin notas'}
                              </td>
                            </tr>
                          )) : (
                            <tr>
                              <td colSpan={3} className="px-6 py-8 text-center text-slate-400">
                                No hay registros de asistencia
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-slate-800">Perfil del Estudiante</h2>
                  <p className="text-slate-500">Información personal y académica</p>
                </div>

                {getSelectedChildInfo() && (
                  <>
                    {/* Student Photo Card */}
                    <Card className="bg-gradient-to-br from-blue-50 to-indigo-50">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-6">
                          <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-white shadow-lg bg-gray-200 flex items-center justify-center">
                            {getSelectedChildInfo().profile_photo_url ? (
                              <img
                                src={`${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api/students/${getSelectedChildInfo().id}/photo`}
                                alt={`${getSelectedChildInfo().first_name} ${getSelectedChildInfo().last_name_paternal}`}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.parentElement.innerHTML = '<div class="w-16 h-16 text-gray-400">👤</div>';
                                }}
                              />
                            ) : (
                              <User className="w-16 h-16 text-gray-400" />
                            )}
                          </div>
                          <div className="flex-1">
                            <h3 className="text-2xl font-bold text-slate-800">
                              {getSelectedChildInfo().first_name} {getSelectedChildInfo().last_name_paternal} {getSelectedChildInfo().last_name_maternal || ''}
                            </h3>
                            <p className="text-lg text-slate-500 mt-1">
                              {getSelectedChildInfo().assigned_grade}
                            </p>
                            <p className="text-sm text-slate-400 mt-2">
                              <span className="font-mono bg-gray-100 px-2 py-1 rounded">
                                {getSelectedChildInfo().student_number}
                              </span>
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card className="modern-card">
                      <CardHeader>
                        <CardTitle className="flex items-center">
                          <User className="w-5 h-5 mr-2 text-blue-600" />
                          Información Personal
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <p className="text-sm text-slate-500">Nombre Completo</p>
                          <p className="font-medium">
                            {getSelectedChildInfo().first_name} {getSelectedChildInfo().last_name_paternal} {getSelectedChildInfo().last_name_maternal || ''}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Número de Estudiante</p>
                          <p className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">{getSelectedChildInfo().student_number}</p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Fecha de Nacimiento</p>
                          <p className="font-medium">
                            {getSelectedChildInfo().birth_date ? 
                              new Date(getSelectedChildInfo().birth_date).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' }) : 'No disponible'
                            }
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Grado Asignado</p>
                          <Badge variant="secondary">{getSelectedChildInfo().assigned_grade}</Badge>
                        </div>
                        {getSelectedChildInfo().phone && (
                          <div>
                            <p className="text-sm text-slate-500">Teléfono</p>
                            <p className="font-medium flex items-center gap-2">
                              <Phone className="w-4 h-4" />
                              {getSelectedChildInfo().phone}
                            </p>
                          </div>
                        )}
                        {getSelectedChildInfo().address && (
                          <div>
                            <p className="text-sm text-slate-500">Dirección</p>
                            <p className="font-medium flex items-center gap-2">
                              <MapPin className="w-4 h-4" />
                              {getSelectedChildInfo().address}
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    <Card className="modern-card">
                      <CardHeader>
                        <CardTitle className="flex items-center">
                          <Heart className="w-5 h-5 mr-2 text-red-600" />
                          Información de Salud
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {getSelectedChildInfo().blood_type && (
                          <div>
                            <p className="text-sm text-slate-500">Tipo de Sangre</p>
                            <p className="font-medium">{getSelectedChildInfo().blood_type}</p>
                          </div>
                        )}
                        {getSelectedChildInfo().allergies && (
                          <div>
                            <p className="text-sm text-slate-500">Alergias</p>
                            <p className="font-medium">{getSelectedChildInfo().allergies}</p>
                          </div>
                        )}
                        {getSelectedChildInfo().medications && (
                          <div>
                            <p className="text-sm text-slate-500">Medicamentos</p>
                            <p className="font-medium">{getSelectedChildInfo().medications}</p>
                          </div>
                        )}
                        {getSelectedChildInfo().medical_notes && (
                          <div>
                            <p className="text-sm text-slate-500">Notas Médicas</p>
                            <p className="font-medium">{getSelectedChildInfo().medical_notes}</p>
                          </div>
                        )}
                        <div>
                          <p className="text-sm text-slate-500">Contacto de Emergencia</p>
                          <p className="font-medium">
                            {getSelectedChildInfo().emergency_contact || 'No disponible'}
                          </p>
                        </div>
                        {getSelectedChildInfo().emergency_phone && (
                          <div>
                            <p className="text-sm text-slate-500">Teléfono de Emergencia</p>
                            <p className="font-medium flex items-center gap-2">
                              <Phone className="w-4 h-4" />
                              {getSelectedChildInfo().emergency_phone}
                            </p>
                          </div>
                        )}
                        {!getSelectedChildInfo().blood_type && !getSelectedChildInfo().allergies && 
                         !getSelectedChildInfo().medications && !getSelectedChildInfo().medical_notes && (
                          <p className="text-sm text-slate-400 italic">Sin información médica registrada</p>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                  </>
                )}
              </div>
            )}

            {activeTab === 'calendars' && <ParentCalendarsView />}
            {activeTab === 'invoices' && <ParentInvoices />}
            {activeTab === 'packages' && <ParentPackages children={children} />}

          </>
        )}
      </div>
    </div>
  );
};

export default ParentDashboard;